import { useState, useEffect } from "react";
import Sidebar from "../../components/layout/Sidebar";
import { useAuth } from "../../context/AuthContext";
import api from "../../utils/api";
import toast from "react-hot-toast";


const roleColor = {
  admin:      { bg: "#ebf8ff", text: "#2b6cb0" },
  accountant: { bg: "#f0fff4", text: "#276749" },
  staff:      { bg: "#f7fafc", text: "#4a5568" },
};

const InviteModal = ({ onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", role: "staff" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {      
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/invite", form);
      toast.success(`Invite sent to ${form.email}`);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send invite");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>Invite Team Member</h2>
          <button onClick={onClose} style={styles.closeBtn}>✕</button>
        </div>
        <form onSubmit={handleSubmit} style={styles.modalForm}>
          <div style={styles.field}>
            <label style={styles.label}>Full Name</label>
            <input style={styles.input} placeholder="Yaw Darko" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Email Address</label>
            <input style={styles.input} type="email" placeholder="yaw@company.com" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Role</label>
            <select style={styles.input} value={form.role} onChange={e => setForm({...form, role: e.target.value})}>
              <option value="staff">Staff — can upload and view invoices</option>
              <option value="accountant">Accountant — can view all + export reports</option>
            </select>
          </div>
          <p style={styles.inviteNote}>They will receive a temporary password to sign in and set up their account.</p>
          <div style={styles.modalFooter}>
            <button type="button" onClick={onClose} style={styles.cancelBtn}>Cancel</button>
            <button type="submit" style={loading ? {...styles.submitBtn, opacity: 0.7} : styles.submitBtn} disabled={loading}>
              {loading ? "Sending..." : "Send Invite"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Team = () => {
  const { user } = useAuth();
  const [team, setTeam] = useState([]);
  const [showInvite, setShowInvite] = useState(false);

  useEffect(() => {
  api.get("/users")
    .then(res => {
      const data = res.data.users;
      setTeam(Array.isArray(data) ? data : []);
    })
    .catch(() => toast.error("Failed to load team"));
  }, []);

  const toggleActive = async (id) => {
    try {
      await api.put(`/users/${id}/deactivate`);
      setTeam(prev => prev.map(u => u._id === id ? { ...u, isActive: !u.isActive } : u));
      toast.success("User status updated");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update status");
    }
  };

  const deleteUser = async (id) => {
  if (!window.confirm("Permanently delete this user? This cannot be undone.")) return;
  try {
     await api.delete(`/users/${id}`);
     setTeam(prev => prev.filter(u => u._id !== id));
     toast.success("User deleted");
   }  catch (err) {
     toast.error(err.response?.data?.message || "Failed to delete user");
   }
  };

  const isAdmin = user?.role === "admin";

  return (
    <div style={styles.shell}>
      <Sidebar />
      <main style={styles.main}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.pageTitle}>Team</h1>
            <p style={styles.pageSub}>{team.filter(u => u.isActive).length} active members</p>
          </div>
          {isAdmin && (
            <button onClick={() => setShowInvite(true)} style={styles.inviteBtn}>+ Invite Member</button>
          )}
        </div>

        {/* Role legend */}
        <div style={styles.legendRow}>
          {[
            { role: "admin",      desc: "Full access — manage team, all invoices, reports" },
            { role: "accountant", desc: "View all invoices, export GRA reports" },
            { role: "staff",      desc: "Upload and view own invoices" },
          ].map(r => {
            const c = roleColor[r.role];
            return (
              <div key={r.role} style={styles.legendCard}>
                <span style={{ ...styles.roleBadge, background: c.bg, color: c.text }}>{r.role}</span>
                <span style={styles.legendDesc}>{r.desc}</span>
              </div>
            );
          })}
        </div>

        {/* Team table */}
        <div style={styles.tableCard}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.thead}>
                {["Member", "Email", "Role", "Last Login", "Status", isAdmin ? "Actions" : ""].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {team.map((member, i) => {
                const rc = roleColor[member.role] || roleColor.staff;
                const isSelf = member.email === user?.email;
                return (
                  <tr key={member._id} style={i % 2 === 0 ? styles.trEven : styles.trOdd}>
                    <td style={styles.td}>
                      <div style={styles.memberCell}>
                        <div style={{ ...styles.memberAvatar, opacity: member.isActive ? 1 : 0.4 }}>
                          {member.name[0]}
                        </div>
                        <div>
                          <div style={styles.memberName}>
                            {member.name}
                            {isSelf && <span style={styles.youTag}>you</span>}
                          </div>
                          <div style={styles.memberJoined}>Joined {new Date(member.createdAt).toLocaleDateString("en-GH")}</div>
                        </div>
                      </div>
                    </td>
                    <td style={styles.td}>{member.email}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.roleBadge, background: rc.bg, color: rc.text }}>{member.role}</span>
                    </td>
                    <td style={styles.td}>
                      {member.lastLogin ? new Date(member.lastLogin).toLocaleDateString("en-GH") : "Never"}
                    </td>
                    <td style={styles.td}>
                      <span style={member.isActive ? styles.activeTag : styles.inactiveTag}>
                        {member.isActive ? "● Active" : "○ Inactive"}
                      </span>
                    </td>
                    {isAdmin && (
                      <td style={styles.td}>
                        {!isSelf && (
                          <div style={{ display: "flex", gap: "8px" }}>
                           <button
                            onClick={() => toggleActive(member._id)}
                            style={member.isActive ? styles.deactivateBtn : styles.activateBtn}
                         >
                           {member.isActive ? "Deactivate" : "Activate"}
                         </button>
                         {!member.isActive && (
                           <button
                            onClick={() => deleteUser(member._id)}
                            style={styles.deleteBtn}
                         >
                          Delete
                         </button>
                         )}
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </main>

      {showInvite && (
        <InviteModal onClose={() => {
          setShowInvite(false);
          api.get("/users")
            .then(res => setTeam(Array.isArray(res.data.users) ? res.data.users : []))
            .catch(() => {});
        }} />
      )}
    </div>
  );
};

const styles = {
  shell: { display: "flex", minHeight: "100vh", background: "#f7f8fc", fontFamily: "sans-serif" },
  main: { flex: 1, padding: "36px 40px", overflowY: "auto" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "24px" },
  pageTitle: { fontSize: "24px", fontWeight: "bold", color: "#0a0f1e", marginBottom: "4px" },
  pageSub: { fontSize: "13px", color: "#718096" },
  inviteBtn: { background: "#0a0f1e", color: "#fff", padding: "10px 20px", borderRadius: "8px", fontSize: "13px", fontWeight: "bold", border: "none", cursor: "pointer" },
  legendRow: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "14px", marginBottom: "24px" },
  legendCard: { background: "#fff", borderRadius: "10px", border: "1px solid #e2e8f0", padding: "14px 16px", display: "flex", alignItems: "center", gap: "10px" },
  legendDesc: { fontSize: "12px", color: "#718096" },
  tableCard: { background: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0", overflow: "hidden" },
  table: { width: "100%", borderCollapse: "collapse" },
  thead: { background: "#f7f8fc" },
  th: { textAlign: "left", padding: "12px 20px", fontSize: "11px", color: "#a0aec0", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: "600", borderBottom: "1px solid #e2e8f0" },
  td: { padding: "16px 20px", fontSize: "13px", color: "#2d3748", verticalAlign: "middle" },
  trEven: { background: "#fff" },
  trOdd: { background: "#fafafa" },
  memberCell: { display: "flex", alignItems: "center", gap: "12px" },
  memberAvatar: { width: "36px", height: "36px", borderRadius: "50%", background: "#f6ad55", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold", fontSize: "14px", color: "#0a0f1e", flexShrink: 0 },
  memberName: { fontWeight: "600", color: "#0a0f1e", fontSize: "13px", display: "flex", alignItems: "center", gap: "6px" },
  memberJoined: { fontSize: "11px", color: "#a0aec0", marginTop: "2px" },
  youTag: { background: "#ebf8ff", color: "#2b6cb0", padding: "1px 6px", borderRadius: "4px", fontSize: "10px", fontWeight: "bold" },
  roleBadge: { padding: "3px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: "600", textTransform: "capitalize" },
  activeTag: { color: "#48bb78", fontWeight: "600", fontSize: "12px" },
  inactiveTag: { color: "#a0aec0", fontWeight: "600", fontSize: "12px" },
  deactivateBtn: { background: "#fff5f5", border: "1px solid #fed7d7", color: "#e53e3e", borderRadius: "6px", padding: "5px 12px", fontSize: "11px", cursor: "pointer", fontWeight: "600" },
  activateBtn: { background: "#f0fff4", border: "1px solid #c6f6d5", color: "#276749", borderRadius: "6px", padding: "5px 12px", fontSize: "11px", cursor: "pointer", fontWeight: "600" },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  modal: { background: "#fff", borderRadius: "16px", width: "460px", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "24px 28px", borderBottom: "1px solid #e2e8f0" },
  modalTitle: { fontSize: "18px", fontWeight: "bold", color: "#0a0f1e" },
  closeBtn: { background: "none", border: "none", fontSize: "18px", cursor: "pointer", color: "#a0aec0" },
  modalForm: { padding: "24px 28px", display: "flex", flexDirection: "column", gap: "16px" },
  field: { display: "flex", flexDirection: "column", gap: "5px" },
  label: { fontSize: "12px", fontWeight: "600", color: "#2d3748" },
  input: { padding: "10px 12px", border: "1.5px solid #e2e8f0", borderRadius: "8px", fontSize: "13px", fontFamily: "sans-serif", outline: "none", background: "#fff", color: "#0a0f1e" },
  inviteNote: { fontSize: "12px", color: "#a0aec0", background: "#f7f8fc", borderRadius: "8px", padding: "10px 12px", margin: 0 },
  modalFooter: { display: "flex", justifyContent: "flex-end", gap: "10px", paddingTop: "8px" },
  cancelBtn: { padding: "10px 20px", background: "#fff", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: "13px", cursor: "pointer", color: "#4a5568" },
  submitBtn: { padding: "10px 24px", background: "#0a0f1e", color: "#fff", border: "none", borderRadius: "8px", fontSize: "13px", fontWeight: "bold", cursor: "pointer" },
  deleteBtn: { background: "#fff5f5", border: "1px solid #fed7d7", color: "#e53e3e", borderRadius: "6px", padding: "5px 12px", fontSize: "11px", cursor: "pointer", fontWeight: "600" },
};

export default Team;